import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { DURATION, EASE } from '../../motion'

interface ModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly title?: string
  readonly children: ReactNode
  readonly footer?: ReactNode
  readonly maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
}

const WIDTH: Record<NonNullable<ModalProps['maxWidth']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl'
}

/**
 * Accessible modal with backdrop. Closes on backdrop click + Esc.
 *
 * Mobile-scroll fix 2026-05-14 (Kiran reported tall forms couldn't be
 * scrolled on phone):
 *   - Panel is capped at viewport height via 100dvh (dynamic vh — the
 *     mobile URL bar collapsing/expanding doesn't truncate the modal).
 *   - Panel is a flex-col: header + footer don't scroll; the body
 *     region in the middle gets overflow-y-auto so long forms (groups
 *     picker, screen-actions multi-select) scroll inside the modal.
 *   - Body scroll lock on <body> still applies so the page underneath
 *     doesn't move while the modal is open.
 *   - On mobile the panel anchors to the bottom (items-end) which is
 *     the familiar bottom-sheet pattern and keeps the close-by-tap
 *     header reachable; on sm+ it centers normally.
 *
 * Mobile footer fix 2026-05-23 (Kiran: Save/Cancel cut off on phone):
 *   - The OVERLAY is sized to the dynamic viewport (h-100dvh), not the
 *     layout viewport (inset-0 = 100vh, which extends behind the mobile
 *     browser's bottom chrome). With items-end the panel — and its
 *     footer — now anchors to the VISIBLE bottom, so the buttons show.
 *   - Footer carries safe-area bottom padding so the buttons also clear
 *     a notch / home indicator.
 *   - Rendered through a portal to <body> so an animated/transformed
 *     ancestor (sidebar, motion wrapper) can't become the containing
 *     block for our position:fixed overlay — which would otherwise
 *     anchor the panel to that element instead of the viewport and clip
 *     the footer.
 *
 * Cinematic 2026-06-08: backdrop fades in, panel rises on the shared EASE
 * curve. Reduced-motion collapses to a fast opacity-only fade.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'md'
}: ModalProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open ? (
        <ModalContent
          onClose={onClose}
          title={title}
          footer={footer}
          maxWidth={maxWidth}
        >
          {children}
        </ModalContent>
      ) : null}
    </AnimatePresence>,
    document.body
  )
}

interface ModalContentProps {
  readonly onClose: () => void
  readonly title: string | undefined
  readonly children: ReactNode
  readonly footer: ReactNode | undefined
  readonly maxWidth: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | undefined
}

function ModalContent({
  onClose,
  title,
  children,
  footer,
  maxWidth
}: ModalContentProps): JSX.Element {
  const reduce = useReducedMotion() === true
  const width = WIDTH[maxWidth ?? 'md']
  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title !== undefined ? 'modal-title' : undefined}
      className="fixed inset-x-0 top-0 h-[100dvh] z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-charcoal/40"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduce ? 0.2 : DURATION.subtle, ease: EASE }}
    >
      <motion.div
        className={`bg-bg-surface border border-border/40 rounded-t-2xl sm:rounded-2xl shadow-lg w-full ${width} flex flex-col max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)] overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: reduce ? 0 : 24, scale: reduce ? 1 : 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: reduce ? 0 : 16, scale: reduce ? 1 : 0.99 }}
        transition={{ duration: reduce ? 0.2 : DURATION.standard, ease: EASE }}
      >
        {title !== undefined ? (
          <header className="shrink-0 px-6 py-4 border-b border-border/40">
            <h2 id="modal-title" className="font-heading text-h3 text-charcoal">
              {title}
            </h2>
          </header>
        ) : null}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 font-body text-body text-text-primary">
          {children}
        </div>
        {footer !== undefined ? (
          <footer className="shrink-0 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-border/40 bg-parchment/50 flex justify-end gap-2">
            {footer}
          </footer>
        ) : null}
      </motion.div>
    </motion.div>
  )
}
